import { useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { VehicleInsurance, CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import type { InsuranceFormRef } from '@makemydeal/dr-shared-components';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { hasBuyerEmailChanged } from '@makemydeal/dr-shared-types';
import type { CardOperationKind, CustomerCardStoreState } from '../types';
import { getCustomerCardBffBaseUrl } from '../config';
import {
    customerCardCancelled,
    customerCardApplyStarted,
    customerCardApplySucceeded,
    customerCardApplyFailed,
    saveParticipantsRSAA,
    syncCustomerRSAA,
    saveInsuranceRSAA,
    filterInsurancePayload
} from '../store/actionCreators';
import { createBusiness, associateBusiness } from '../store/businessConversionActionCreators';
import * as businessConversionSelectors from '../store/businessConversionSelectors';
import { BUSINESS, CARD_OPERATION_KIND } from '../constants';
import { SPD_FIELDS } from '../components/sections/spd/spd.utils';
import { mapFormValuesToCreateBusinessPayload, mapFormValuesToAssociateBusinessPayload } from '../mappers/businessConversionMapper';
import { mapFormValuesToCreateConsumerPayload } from '../mappers/consumerMapper';
import { createConsumer } from '../store/consumerActionCreators';
import { saveCustomerSpdRSAA, resetCustomerSpdLoadState } from '../store/customerSpdActionCreators';
import { getSpdSaveError } from '../store/customerSpdSelectors';
import { buildParticipantsPayload } from '../mappers/participantsMapper';
import type { SaveParticipantsPayload } from '../mappers/participantsMapper';
import { toSyncCustomerData } from '../mappers/syncCustomerMapper';
import type { SyncCustomerData, SyncCustomerSpd } from '../mappers/syncCustomerMapper';
import * as customerCardSelectors from '../store/selectors';
import { useCustomerCardSelector } from './useCustomerCardSelector';
import { logCustomerCardInsuranceFormEvent, logCustomerCardPageAction } from '../utils/newRelicLogging';
import type { CustomerCardSaveData, CustomerFormRef, CustomerFormValues, CustomerSpdSaveItem, CustomerSpdValues } from '../types';

type FormRefs = {
    buyerRef: RefObject<CustomerFormRef | null>;
    coBuyerRef?: RefObject<CustomerFormRef | null>;
    insuranceRef?: RefObject<InsuranceFormRef | null>;
};

type UseCustomerCardActionsOptions = {
    formRefs: FormRefs;
    dealId?: string;
    onSave?: (data: CustomerCardSaveData) => void;
    onBusinessConversionComplete?: (data: { commonConsumerId: string; businessEntityId: string }) => void;
    confirmBuyerEmailChange?: () => Promise<boolean>;
};

const SAVE_ERROR_MESSAGE = 'Failed to save customer data. Please try again.';
const SAVE_INSURANCE_ERROR_MESSAGE = 'Failed to save insurance data. Please try again.';
const SAVE_ALL_ERROR_MESSAGE = 'Failed to save customer and insurance data. Please try again.';
const BUSINESS_CONVERSION_ERROR_MESSAGE = 'Unable to convert this customer to business. Please try again.';
const CREATE_CONSUMER_ERROR_MESSAGE = 'Unable to create this customer. Please try again.';

/** Moves SPD items from a participant's old id to their new one. */
const restampSpdIds = <T extends { commonConsumerId: string }>(items: T[], from?: string, to?: string): T[] =>
    from && to && from !== to
        ? items.map((item) => (item.commonConsumerId === from ? { ...item, commonConsumerId: to } : item))
        : items;

/** One participant's changed SPD, trimmed to the fields Common Consumer accepts. */
const toSyncSpd = (item: CustomerSpdSaveItem): SyncCustomerSpd =>
    Object.fromEntries(SPD_FIELDS.filter((field) => item[field] != null).map((field) => [field, item[field]])) as SyncCustomerSpd;

/** Raw SPD values keyed by participant, for the host callback. */
const toSpdValuesByCcid = (items: CustomerSpdSaveItem[]): Record<string, CustomerSpdValues> =>
    items.reduce((acc, { commonConsumerId, ...values }) => {
        acc[commonConsumerId] = { ...acc[commonConsumerId], ...values };
        return acc;
    }, {} as Record<string, CustomerSpdValues>);

/**
 * Shared apply/cancel orchestration used by both CustomerCard and UnifiedCustomerCard.
 *
 * CustomerCard passes a single-element formRefs array (buyer or coBuyer).
 * UnifiedCustomerCard passes [buyerFormRef, coBuyerFormRef, insuranceFormRef] so both forms are
 * validated and their values merged into a single apply call.
 */
export const useCustomerCardActions = ({
    formRefs,
    dealId,
    onSave,
    onBusinessConversionComplete,
    confirmBuyerEmailChange
}: UseCustomerCardActionsOptions) => {
    const dispatch = useDispatch();
    const store = useStore();
    const customerCardState = useCustomerCardSelector(customerCardSelectors.getCustomerCardState);
    const isSyncCustomerCardFieldsEnabled = useSelector(featureToggleSelectors.isSyncCustomerCardFieldsEnabled);
    const isSyncSpdFieldsEnabled = useSelector(featureToggleSelectors.isSyncSpdFieldsEnabled);
    const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);
    const inFlightRef = useRef(false);

    const handleBusinessConversion = useCallback(
        async (buyer: CustomerFormValues): Promise<CustomerFormValues> => {
            const selectedBusinessEntityId = businessConversionSelectors.getSelectedBusinessEntityId(
                store.getState() as CustomerCardStoreState
            );

            let conversionResult: { ok: boolean; data?: any; error?: string };

            if (selectedBusinessEntityId) {
                const buyerWithEntityId = {
                    ...buyer,
                    business: { ...buyer.business, businessEntityId: selectedBusinessEntityId }
                };
                conversionResult = (await dispatch(
                    associateBusiness(mapFormValuesToAssociateBusinessPayload(buyerWithEntityId, enableCorrectPhoneHandling))
                )) as any;
            } else {
                conversionResult = (await dispatch(
                    createBusiness(mapFormValuesToCreateBusinessPayload(buyer, enableCorrectPhoneHandling))
                )) as any;
            }

            if (!conversionResult.ok) {
                throw new Error(conversionResult.error || BUSINESS_CONVERSION_ERROR_MESSAGE);
            }

            return {
                ...buyer,
                commonConsumerId: conversionResult.data.commonConsumerId,
                business: { ...buyer.business, businessEntityId: conversionResult.data.businessEntityId },
                customerType: BUSINESS
            };
        },
        [dispatch, store, enableCorrectPhoneHandling]
    );

    /**
     * Resolves who a drafted participant actually is. A draft with an id is an existing record; one
     * without is a participant the dealer typed by hand, so the record is created and the returned id
     * is what the deal links to. Throws on failure so the caller owns the apply-failed dispatch.
     */
    const resolveDraftedIdentity = useCallback(
        async (
            values: CustomerFormValues,
            draft: IShopper | CoBuyer | null,
            kind: CardOperationKind | null
        ): Promise<CustomerFormValues> => {
            if (!draft) return values;
            if (kind === CARD_OPERATION_KIND.CONVERT_TO_BUSINESS) return handleBusinessConversion(values);
            if (values.commonConsumerId) return values;

            const created = (await dispatch(
                createConsumer(mapFormValuesToCreateConsumerPayload(values, enableCorrectPhoneHandling))
            )) as any;
            if (!created.ok) throw new Error(created.error || CREATE_CONSUMER_ERROR_MESSAGE);
            return { ...values, commonConsumerId: created.commonConsumerId };
        },
        [dispatch, handleBusinessConversion, enableCorrectPhoneHandling]
    );

    const apply = useCallback(async () => {
        // Prevent concurrent apply calls from racing each other and causing double saves.
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        let buyer: CustomerFormValues | undefined;
        let coBuyer: CustomerFormValues | null = null;
        let insurance: VehicleInsurance | null = null;
        let saveResult: Partial<CustomerCardSaveData> = {};
        try {
            const { buyerRef, coBuyerRef, insuranceRef } = formRefs;

            const allRefs = [buyerRef, coBuyerRef, insuranceRef].filter(Boolean) as RefObject<
                (CustomerFormRef | InsuranceFormRef) | null
            >[];
            const participantRefs = [buyerRef, coBuyerRef].filter(Boolean) as RefObject<CustomerFormRef | null>[];

            const validations = await Promise.all(allRefs.map((r) => r.current?.validate() ?? Promise.resolve(true)));
            if (validations.some((valid) => !valid)) return;

            const isInsuranceDirty = insuranceRef?.current?.isDirty() ?? false;
            let isParticipantsDirty = participantRefs.some((r) => r.current?.isDirty() ?? false);

            // Collect values from each form ref in order [buyer, coBuyer?]
            const [buyerValues, coBuyerValues] = participantRefs.map((r) => r.current?.getValues());

            // buyer ref not attached — form hasn't mounted yet, nothing to save.
            if (!buyerValues) return;

            // A drafted participant replaces the seeded one, so identity and the sync baseline both
            // come from the draft. Without this the save writes the new person onto the old record.
            const applyState = store.getState() as CustomerCardStoreState;
            const buyerDraft = customerCardSelectors.getBuyerDraft(applyState);
            const coBuyerDraft = customerCardSelectors.getCoBuyerDraft(applyState);
            const operationKind = customerCardSelectors.getOperationKind(applyState);
            const buyerIdentity = buyerDraft ?? customerCardState?.buyer;
            const coBuyerIdentity = coBuyerDraft ?? customerCardState?.coBuyer;
            const replaced = {
                buyer: operationKind === CARD_OPERATION_KIND.CHANGE_BUYER,
                coBuyer: operationKind === CARD_OPERATION_KIND.CHANGE_CO_BUYER
            };

            // Merge: an edit seeds from the full stored record so store-only fields survive the save.
            // Replace: a drafted participant is seeded from the draft alone, so nothing carries over from the
            // replaced person. Business conversion's draft already spreads the stored record, so it still merges.
            buyer = {
                ...(!buyerDraft && (customerCardState?.buyer as Omit<IShopper, keyof CustomerFormValues> | undefined)),
                ...buyerValues,
                commonConsumerId: buyerIdentity?.commonConsumerId,
                customerType: buyerValues.customerType || buyerIdentity?.customerType,
                ...((customerCardState?.buyer?.business || buyerValues.business) && {
                    business: { ...customerCardState?.buyer?.business, ...buyerValues.business }
                })
            };
            coBuyer = coBuyerValues
                ? {
                      ...(!coBuyerDraft && (customerCardState?.coBuyer as Omit<CoBuyer, keyof CustomerFormValues> | undefined)),
                      ...coBuyerValues,
                      commonConsumerId: coBuyerIdentity?.commonConsumerId,
                      customerType: coBuyerIdentity?.customerType
                  }
                : null;

            const bffBaseUrl = getCustomerCardBffBaseUrl();
            if (!bffBaseUrl || !dealId) {
                dispatch(customerCardApplyFailed(SAVE_ERROR_MESSAGE));
                return;
            }

            if (isInsuranceDirty) {
                insurance = insuranceRef!.current!.getValues();
            }

            // Collect changed SPD values per participant; bankAccountNumber saves via the participants endpoint, not SPD.
            // A participant the dealer is creating has no id yet, so the role is carried and stamped after resolution.
            const spdCandidates = [
                { role: 'buyer' as const, ref: buyerRef, commonConsumerId: buyerIdentity?.commonConsumerId },
                ...(coBuyerRef
                    ? [{ role: 'coBuyer' as const, ref: coBuyerRef, commonConsumerId: coBuyerIdentity?.commonConsumerId }]
                    : [])
            ];
            const dirtySpd = spdCandidates
                .filter(({ ref }) => ref.current?.isSpdDirty())
                .map(({ role, ref, commonConsumerId }) => ({ role, commonConsumerId, ...ref.current!.getSpdValues() }));
            const isSpdDirty = dirtySpd.length > 0;

            // Force the participants save when SPD changed, or a newly-added co-buyer with only SPD edits never gets their participant record saved.
            isParticipantsDirty = isParticipantsDirty || isSpdDirty || Boolean(buyerDraft || coBuyerDraft);
            if (!isParticipantsDirty && !isInsuranceDirty && !isSpdDirty) return;

            if (
                confirmBuyerEmailChange &&
                hasBuyerEmailChanged(customerCardState?.buyer?.email, buyer.email) &&
                !(await confirmBuyerEmailChange())
            ) {
                return;
            }

            const isBusinessConversionInProgress = businessConversionSelectors.getIsBusinessEditStep(
                store.getState() as CustomerCardStoreState
            );

            dispatch(customerCardApplyStarted());

            const previousBuyerId = buyer.commonConsumerId;
            const previousCoBuyerId = coBuyer?.commonConsumerId;

            try {
                buyer = await resolveDraftedIdentity(buyer, buyerDraft, operationKind);
                if (coBuyer) coBuyer = await resolveDraftedIdentity(coBuyer, coBuyerDraft, operationKind);
            } catch (err) {
                dispatch(customerCardApplyFailed((err as Error).message));
                return;
            }

            // SPD was collected before identity was resolved, so a created participant's items get the id
            // that resolution minted, and the rest are moved off any id it replaced.
            const resolvedSpdId = { buyer: buyer.commonConsumerId, coBuyer: coBuyer?.commonConsumerId };
            let spdItems = dirtySpd
                .map(({ role, commonConsumerId, ...values }) => ({
                    commonConsumerId: commonConsumerId ?? resolvedSpdId[role],
                    ...values
                }))
                .filter((item): item is typeof item & { commonConsumerId: string } => Boolean(item.commonConsumerId));
            spdItems = restampSpdIds(spdItems, previousBuyerId, buyer.commonConsumerId);
            spdItems = restampSpdIds(spdItems, previousCoBuyerId, coBuyer?.commonConsumerId);

            const state = store.getState();
            const dealerUser = customerCardSelectors.getBridgeUsername(state as CustomerCardStoreState);
            const dealerId = String(customerCardSelectors.getDealerId(state as CustomerCardStoreState) ?? '');
            const isConsolidatedSaveEnabled = featureToggleSelectors.isCustomerCardSaveConsolidationEnabled(state as StateTree);

            // syncCustomer reflects only what actually saved, behind F313128_Sync_Customer_Card_Fields.
            const buildSyncCustomers = (
                savedBuyer: CustomerFormValues,
                includeParticipants: boolean,
                syncInsurance: boolean,
                syncSpd: boolean
            ): SyncCustomerData[] => {
                // Buyer and co-buyer edit SPD independently, so each gets their own changed SPD.
                const findSpd = (commonConsumerId?: string): SyncCustomerSpd | null => {
                    if (!syncSpd) return null;

                    const item = spdItems.find((spdItem) => spdItem.commonConsumerId === commonConsumerId);
                    return item ? toSyncSpd(item) : null;
                };

                return [
                    {
                        form: includeParticipants ? savedBuyer : { commonConsumerId: savedBuyer.commonConsumerId },
                        original: buyerIdentity,
                        insurance: syncInsurance ? insurance : null,
                        spd: findSpd(savedBuyer.commonConsumerId)
                    },
                    ...(coBuyer && includeParticipants
                        ? [{ form: coBuyer, original: coBuyerIdentity, insurance: null, spd: findSpd(coBuyer.commonConsumerId) }]
                        : [])
                ]
                    .filter(({ form }) => form.commonConsumerId)
                    .map((data) =>
                        toSyncCustomerData(data.form, data.original, data.insurance, data.spd, enableCorrectPhoneHandling)
                    );
            };

            // Save requests. Each toggle path reports which sections failed and what to sync; the outcome is handled below.
            let participantsFailed: boolean;
            let spdFailed = false;
            let insuranceFailed: boolean;
            let syncCustomers: SyncCustomerData[] = [];

            const saveStartedAt = Date.now();

            if (isConsolidatedSaveEnabled) {
                // SPD only when dirty, so the BFF can skip the dealershipMember permission check; insurance omitted only when there's nothing to send.
                // TODO: move SPD dirty check to the BFF — it already fetches the DealSPD and can diff it directly.
                const insurancePayload =
                    insurance ?? (customerCardState?.vehicleInsuranceInfo ? insuranceRef?.current?.getValues() ?? null : null);

                const payload: SaveParticipantsPayload = {
                    ...buildParticipantsPayload(buyer, coBuyer, dealerUser, enableCorrectPhoneHandling),
                    ...(isSpdDirty && { spd: spdItems }),
                    ...(insurancePayload && { vehicleInsurance: filterInsurancePayload(insurancePayload) })
                };
                await dispatch(saveParticipantsRSAA(bffBaseUrl, dealId, dealerId, payload));

                // One request, one outcome — the response can't tell us which section failed, so every
                // section shares it.
                participantsFailed = Boolean(
                    customerCardSelectors.getSaveParticipantsError(store.getState() as CustomerCardStoreState)
                );
                insuranceFailed = isInsuranceDirty && participantsFailed;
                spdFailed = isSpdDirty && participantsFailed;

                const shouldSyncInsurance = isSyncCustomerCardFieldsEnabled && isInsuranceDirty && !insuranceFailed;
                const shouldSyncSpd = isSyncSpdFieldsEnabled && !spdFailed;
                if (!participantsFailed) {
                    syncCustomers = buildSyncCustomers(buyer, true, shouldSyncInsurance, shouldSyncSpd);
                }
            } else {
                // Save participants, SPD, and insurance in parallel (only when dirty).
                const savePromises: Promise<unknown>[] = [];
                if (isParticipantsDirty) {
                    savePromises.push(
                        dispatch(
                            saveParticipantsRSAA(
                                bffBaseUrl,
                                dealId,
                                dealerId,
                                buildParticipantsPayload(buyer, coBuyer, dealerUser, enableCorrectPhoneHandling)
                            )
                        ) as Promise<unknown>
                    );
                }
                if (isSpdDirty) {
                    savePromises.push(dispatch(saveCustomerSpdRSAA(spdItems)) as Promise<unknown>);
                }
                if (isInsuranceDirty) {
                    savePromises.push(dispatch(saveInsuranceRSAA(bffBaseUrl, dealId, dealerId, insurance!)) as Promise<unknown>);
                }
                await Promise.all(savePromises);

                // A request per section — each reports its own outcome.
                const postSaveState = store.getState() as CustomerCardStoreState;
                participantsFailed = isParticipantsDirty && Boolean(customerCardSelectors.getSaveParticipantsError(postSaveState));
                spdFailed = isSpdDirty && Boolean(getSpdSaveError(postSaveState));
                insuranceFailed = isInsuranceDirty && Boolean(customerCardSelectors.getSaveInsuranceError(postSaveState));

                // A request per section, so sync only the sections that saved. Participants failing still
                // syncs insurance, identified by commonConsumerId alone.
                const shouldSyncInsurance = isSyncCustomerCardFieldsEnabled && isInsuranceDirty && !insuranceFailed;
                const shouldSyncSpd = isSyncSpdFieldsEnabled && isSpdDirty && !spdFailed;
                if (!participantsFailed || shouldSyncInsurance || shouldSyncSpd) {
                    syncCustomers = buildSyncCustomers(buyer, !participantsFailed, shouldSyncInsurance, shouldSyncSpd);
                }
            }

            const failedCount = [participantsFailed, spdFailed, insuranceFailed].filter(Boolean).length;
            const savedAny =
                !isConsolidatedSaveEnabled &&
                failedCount < [isParticipantsDirty, isSpdDirty, isInsuranceDirty].filter(Boolean).length;

            logCustomerCardPageAction('CustomerCardSaveCompleted', {
                durationMs: Date.now() - saveStartedAt,
                isCustomerCardSaveConsolidationEnabled: isConsolidatedSaveEnabled,
                result: failedCount === 0 ? 'success' : savedAny ? 'partial' : 'failure',
                dealXgId: dealId,
                dealerId
            });

            // Mirrors the legacy InsuranceFormContainer's <hostApp>:InsuranceForm Save event, ported
            // here (rather than InsuranceTab) because this hook — not the insurance component — owns
            // the actual save call in both the consolidated and per-section paths.
            if (isInsuranceDirty) {
                logCustomerCardInsuranceFormEvent({
                    event: 'Save',
                    status: insuranceFailed ? 'failure' : 'success',
                    dealXgId: dealId,
                    dealerId,
                    ...(insuranceFailed && { updateError: SAVE_INSURANCE_ERROR_MESSAGE })
                });
            }

            // Fire-and-forget sync.
            if (syncCustomers.length > 0) {
                void dispatch(syncCustomerRSAA(bffBaseUrl, dealId, dealerId, syncCustomers));
            }

            // Notify host of business conversion completion only after participants saved successfully.
            // Clearing the operation is APPLY_SUCCEEDED's job, so there is nothing to dispatch here.
            if (isBusinessConversionInProgress && !participantsFailed) {
                onBusinessConversionComplete?.({
                    commonConsumerId: buyer.commonConsumerId!,
                    businessEntityId: buyer.business!.businessEntityId ?? ''
                });
            }

            if (isConsolidatedSaveEnabled) {
                // One request, so the deal either saved or it didn't — exactly one action fires.
                if (participantsFailed) {
                    dispatch(customerCardApplyFailed(SAVE_ERROR_MESSAGE));
                    return;
                }

                participantRefs.forEach((r) => r.current!.reset());
                saveResult = {
                    buyer,
                    coBuyer,
                    replaced,
                    insurance,
                    spdValues: toSpdValuesByCcid(spdItems)
                };

                dispatch(customerCardApplySucceeded(saveResult.buyer, saveResult.coBuyer, saveResult.insurance));
            } else {
                // A request per section, so sections can disagree — a partial success reports both.
                const errors: string[] = [];

                if (isParticipantsDirty) {
                    if (participantsFailed) {
                        errors.push(SAVE_ERROR_MESSAGE);
                    } else {
                        participantRefs.forEach((r) => r.current!.reset());
                        saveResult.buyer = buyer;
                        saveResult.coBuyer = coBuyer;
                        saveResult.replaced = replaced;
                    }
                }
                if (spdFailed) {
                    errors.push(SAVE_ERROR_MESSAGE);
                } else if (isSpdDirty) {
                    saveResult.spdValues = toSpdValuesByCcid(spdItems);
                }
                if (isInsuranceDirty) {
                    if (insuranceFailed) {
                        errors.push(SAVE_INSURANCE_ERROR_MESSAGE);
                    } else {
                        saveResult.insurance = insurance;
                    }
                }

                // Merge whatever saved successfully back into the activity store.
                // On partial success the subsequent applyFailed overwrites saveStatus.
                dispatch(customerCardApplySucceeded(saveResult.buyer, saveResult.coBuyer, saveResult.insurance));

                if (errors.length > 0) {
                    if (Object.keys(saveResult).length > 0) {
                        onSave?.({ ...saveResult, allSaved: false });
                    }
                    dispatch(customerCardApplyFailed(errors.length === 1 ? errors[0] : SAVE_ALL_ERROR_MESSAGE));
                    return;
                }
            }
            dispatch(resetCustomerSpdLoadState());
        } catch {
            dispatch(customerCardApplyFailed(SAVE_ERROR_MESSAGE));
        } finally {
            inFlightRef.current = false;
        }
        // Call onSave outside the save try-catch: errors in the success callback must not
        // surface as a save error (the save itself already succeeded).
        onSave?.({ ...saveResult, allSaved: true });
    }, [
        formRefs,
        dealId,
        onSave,
        onBusinessConversionComplete,
        confirmBuyerEmailChange,
        handleBusinessConversion,
        dispatch,
        customerCardState,
        store,
        isSyncCustomerCardFieldsEnabled,
        isSyncSpdFieldsEnabled,
        enableCorrectPhoneHandling
    ]);

    const cancel = useCallback(() => {
        dispatch(customerCardCancelled());
        dispatch(resetCustomerSpdLoadState());
    }, [dispatch]);

    return { apply, cancel, customerCardState };
};
